// Preview-mode data store. Everything is saved in this browser only (localStorage) until the
// Supabase database is connected (docs/03-PLAN.md, Stage 4). Pages only use the methods below,
// so the database version can replace this file without touching the pages.
// Every check here is repeated by database rules on the server; this copy only gives fast feedback.
import { useSyncExternalStore } from 'react';
import { z } from 'zod';
import { CommentInput, CommunityInput, fieldErrors, IdeaInput, PostInput, ProfileInput, ReportInput } from '../lib/validation';
import { extractTags, slugify } from '../lib/text';
import { seed } from './seed';
import type { Comment, Community, DB, FollowKind, ID, Idea, Notification, Post, Report, Settings, User } from './types';

export const STORAGE_KEY = 'nostalgia-preview-v1';
export const MAX_REPLY_DEPTH = 3;
export type Result<T> = { ok: true; value: T } | { ok: false; error: string; fields?: Record<string, string> };
export const NEED_PROFILE = 'NEED_PROFILE';

const fail = <T>(error: string, fields?: Record<string, string>): Result<T> => ({ ok: false, error, fields });
const okv = <T>(value: T): Result<T> => ({ ok: true, value });
function uid(prefix: string): string {
  const r = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
  return `${prefix}-${r}`;
}
function parse<S extends z.ZodType>(schema: S, input: unknown): Result<z.infer<S>> {
  const r = schema.safeParse(input);
  return r.success ? okv(r.data) : fail('Please check the highlighted fields.', fieldErrors(r.error));
}

function load(): DB {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const db = JSON.parse(raw) as DB;
      const lists = ['users', 'posts', 'comments', 'reactions', 'saves', 'follows', 'communities', 'memberships', 'ideas', 'reports', 'notifications'] as const;
      if (db && db.version === 1 && lists.every(k => Array.isArray(db[k])) && db.settings) return { ...db, settings: { ...seed(0).settings, ...db.settings } };
    }
  } catch { /* storage blocked or corrupt: start fresh */ }
  return seed(Date.now());
}

export class Store {
  private state: DB;
  private listeners = new Set<() => void>();
  constructor(initial?: DB) { this.state = initial ?? load(); }

  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  get = (): DB => this.state;
  me = (): User | undefined => this.state.users.find(u => u.id === this.state.meId);

  private commit(mutate: (d: DB) => void): void {
    const next = structuredClone(this.state);
    mutate(next);
    this.state = next;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* private mode: keep in memory */ }
    this.listeners.forEach(l => l());
  }
  private notify(d: DB, n: Omit<Notification, 'id' | 'createdAt' | 'read'>): void {
    if (n.userId === n.actorId) return;
    d.notifications.unshift({ ...n, id: uid('n'), createdAt: Date.now(), read: false });
    d.notifications = d.notifications.slice(0, 200);
  }

  /* ---------- profile ---------- */
  saveProfile(input: unknown): Result<User> {
    const p = parse(ProfileInput, input); if (!p.ok) return p;
    const me = this.me();
    if (this.state.users.some(u => u.handle === p.value.handle && u.id !== me?.id)) return fail('That username is taken.', { handle: 'That username is taken.' });
    const user: User = { ...(me ?? { id: uid('u'), joinedAt: Date.now() }), ...p.value };
    this.commit(d => { const i = d.users.findIndex(u => u.id === user.id); if (i >= 0) d.users[i] = user; else d.users.push(user); d.meId = user.id; });
    return okv(user);
  }

  /* ---------- posts ---------- */
  createPost(input: unknown): Result<Post> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    const p = parse(PostInput, input); if (!p.ok) return p;
    const v = p.value;
    if (v.communityId && !this.state.communities.some(c => c.id === v.communityId)) return fail('That community does not exist.');
    if (v.ideaId && !this.state.ideas.some(i => i.id === v.ideaId)) return fail('That idea does not exist.');
    const post: Post = { id: uid('p'), authorId: me.id, ...v, solved: false, tags: extractTags(v.title + ' ' + v.body), createdAt: Date.now() };
    this.commit(d => {
      d.posts.unshift(post);
      const idea = v.ideaId ? d.ideas.find(i => i.id === v.ideaId) : undefined;
      if (idea) this.notify(d, { userId: idea.authorId, actorId: me.id, type: 'answer', postId: post.id });
    });
    return okv(post);
  }
  editPost(id: ID, patch: { title: string; body: string }): Result<Post> {
    const me = this.me(), post = this.state.posts.find(x => x.id === id);
    if (!me || !post || post.authorId !== me.id || post.removed) return fail('You can only edit your own memories.');
    const p = parse(PostInput.pick({ title: true, body: true }), patch); if (!p.ok) return p;
    const next = { ...post, ...p.value, tags: extractTags(p.value.title + ' ' + p.value.body), editedAt: Date.now() };
    this.commit(d => { d.posts = d.posts.map(x => (x.id === id ? next : x)); });
    return okv(next);
  }
  deletePost(id: ID): Result<true> {
    const me = this.me(), post = this.state.posts.find(x => x.id === id);
    if (!me || !post || post.authorId !== me.id) return fail('You can only delete your own memories.');
    this.commit(d => {
      d.posts = d.posts.filter(x => x.id !== id);
      d.comments = d.comments.filter(c => c.postId !== id);
      d.reactions = d.reactions.filter(r => r.postId !== id);
      d.saves = d.saves.filter(s => s.postId !== id);
      d.notifications = d.notifications.filter(n => n.postId !== id);
    });
    return okv(true);
  }
  markSolved(id: ID): Result<true> {
    const me = this.me(), post = this.state.posts.find(x => x.id === id);
    if (!me || !post || post.authorId !== me.id || post.format !== 'forgotten') return fail('Only the person who asked can mark it as found.');
    this.commit(d => { const x = d.posts.find(q => q.id === id); if (x) x.solved = true; });
    return okv(true);
  }

  /* ---------- reactions, saves ---------- */
  toggleReaction(postId: ID): Result<boolean> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    const post = this.state.posts.find(p => p.id === postId); if (!post) return fail('That memory is gone.');
    const on = this.state.reactions.some(r => r.postId === postId && r.userId === me.id);
    this.commit(d => {
      if (on) d.reactions = d.reactions.filter(r => !(r.postId === postId && r.userId === me.id));
      else { d.reactions.push({ postId, userId: me.id }); this.notify(d, { userId: post.authorId, actorId: me.id, type: 'reaction', postId }); }
    });
    return okv(!on);
  }
  toggleSave(postId: ID): Result<boolean> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    if (!this.state.posts.some(p => p.id === postId)) return fail('That memory is gone.');
    const on = this.state.saves.some(s => s.postId === postId && s.userId === me.id);
    this.commit(d => {
      if (on) d.saves = d.saves.filter(s => !(s.postId === postId && s.userId === me.id));
      else d.saves.push({ postId, userId: me.id });
    });
    return okv(!on);
  }

  /* ---------- comments ---------- */
  addComment(postId: ID, input: unknown, parentId?: ID): Result<Comment> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    const p = parse(CommentInput, input); if (!p.ok) return p;
    const post = this.state.posts.find(x => x.id === postId); if (!post) return fail('That memory is gone.');
    let parent: Comment | undefined;
    if (parentId) {
      parent = this.state.comments.find(c => c.id === parentId && c.postId === postId);
      if (!parent) return fail('That comment is gone.');
      if (depthOf(this.state, parent) >= MAX_REPLY_DEPTH - 1) parent = parent.parentId ? this.state.comments.find(c => c.id === parent!.parentId) : parent;
    }
    const comment: Comment = { id: uid('c'), postId, parentId: parent?.id, authorId: me.id, body: p.value.body, createdAt: Date.now() };
    this.commit(d => {
      d.comments.push(comment);
      this.notify(d, { userId: post.authorId, actorId: me.id, type: 'comment', postId });
      if (parent && parent.authorId !== post.authorId) this.notify(d, { userId: parent.authorId, actorId: me.id, type: 'reply', postId });
    });
    return okv(comment);
  }
  deleteComment(id: ID): Result<true> {
    const me = this.me(), c = this.state.comments.find(x => x.id === id);
    if (!me || !c || c.authorId !== me.id) return fail('You can only delete your own comments.');
    // Keep the slot so replies under it still make sense.
    this.commit(d => { const x = d.comments.find(q => q.id === id); if (x) { x.removed = true; x.body = ''; } });
    return okv(true);
  }

  /* ---------- follows ---------- */
  toggleFollow(kind: FollowKind, target: string): Result<boolean> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    if (kind === 'user' && target === me.id) return fail('You cannot follow yourself.');
    const on = this.state.follows.some(f => f.userId === me.id && f.kind === kind && f.target === target);
    this.commit(d => {
      if (on) d.follows = d.follows.filter(f => !(f.userId === me.id && f.kind === kind && f.target === target));
      else { d.follows.push({ userId: me.id, kind, target }); if (kind === 'user') this.notify(d, { userId: target, actorId: me.id, type: 'follow' }); }
    });
    return okv(!on);
  }

  /* ---------- communities ---------- */
  createCommunity(input: unknown): Result<Community> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    const p = parse(CommunityInput, input); if (!p.ok) return p;
    const slug = slugify(p.value.name);
    if (slug.length < 3) return fail('Use a name with at least 3 letters or numbers.', { name: 'Use a name with at least 3 letters or numbers.' });
    if (this.state.communities.some(c => c.slug === slug)) return fail('A community with that name already exists.', { name: 'A community with that name already exists.' });
    const c: Community = { id: uid('cm'), slug, ...p.value, ownerId: me.id, createdAt: Date.now() };
    this.commit(d => { d.communities.unshift(c); d.memberships.push({ communityId: c.id, userId: me.id, role: 'owner' }); });
    return okv(c);
  }
  toggleMembership(communityId: ID): Result<boolean> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    const m = this.state.memberships.find(x => x.communityId === communityId && x.userId === me.id);
    if (m?.role === 'owner') return fail('You started this community, so you cannot leave it.');
    this.commit(d => {
      if (m) d.memberships = d.memberships.filter(x => !(x.communityId === communityId && x.userId === me.id));
      else d.memberships.push({ communityId, userId: me.id, role: 'member' });
    });
    return okv(!m);
  }

  /* ---------- ideas ---------- */
  createIdea(input: unknown): Result<Idea> {
    const me = this.me(); if (!me) return fail(NEED_PROFILE);
    const p = parse(IdeaInput, input); if (!p.ok) return p;
    const idea: Idea = { id: uid('i'), authorId: me.id, ...p.value, tags: extractTags(p.value.text), createdAt: Date.now() };
    this.commit(d => { d.ideas.unshift(idea); });
    return okv(idea);
  }

  /* ---------- safety ---------- */
  report(targetType: Report['targetType'], targetId: ID, input: unknown): Result<Report> {
    const p = parse(ReportInput, input); if (!p.ok) return p;
    const r: Report = { id: uid('r'), reporterId: this.me()?.id, targetType, targetId, ...p.value, createdAt: Date.now(), status: 'open' };
    this.commit(d => { d.reports.push(r); });
    return okv(r);
  }

  /* ---------- settings and data ---------- */
  setSettings(patch: Partial<Settings>): void { this.commit(d => { d.settings = { ...d.settings, ...patch }; }); }
  markNotificationsRead(): void {
    const me = this.me(); if (!me) return;
    this.commit(d => { d.notifications.forEach(n => { if (n.userId === me.id) n.read = true; }); });
  }
  /** Everything this person created, as JSON (the "download my data" right). */
  exportMine(): string {
    const me = this.me(); const s = this.state;
    if (!me) return '{}';
    return JSON.stringify({
      profile: me,
      memories: s.posts.filter(p => p.authorId === me.id),
      comments: s.comments.filter(c => c.authorId === me.id),
      reactions: s.reactions.filter(r => r.userId === me.id),
      saved: s.saves.filter(x => x.userId === me.id),
      follows: s.follows.filter(f => f.userId === me.id),
      communities: s.communities.filter(c => c.ownerId === me.id),
      ideas: s.ideas.filter(i => i.authorId === me.id),
    }, null, 2);
  }
  /** Deletes this person's profile and everything they made. */
  deleteMe(): void {
    const me = this.me(); if (!me) return;
    const mine = new Set(this.state.posts.filter(p => p.authorId === me.id).map(p => p.id));
    this.commit(d => {
      d.users = d.users.filter(u => u.id !== me.id);
      d.posts = d.posts.filter(p => !mine.has(p.id));
      d.comments = d.comments.filter(c => !mine.has(c.postId));
      // Keep an empty slot where others replied, so their replies stay visible; drop the rest.
      const parents = new Set(d.comments.filter(c => c.parentId).map(c => c.parentId));
      d.comments = d.comments.filter(c => c.authorId !== me.id || parents.has(c.id))
        .map(c => (c.authorId === me.id ? { ...c, authorId: 'deleted', body: '', removed: true } : c));
      d.reactions = d.reactions.filter(r => r.userId !== me.id && !mine.has(r.postId));
      d.saves = d.saves.filter(s => s.userId !== me.id && !mine.has(s.postId));
      d.follows = d.follows.filter(f => f.userId !== me.id && !(f.kind === 'user' && f.target === me.id));
      const owned = new Set(d.communities.filter(c => c.ownerId === me.id).map(c => c.id));
      d.communities = d.communities.filter(c => !owned.has(c.id));
      d.posts.forEach(p => { if (p.communityId && owned.has(p.communityId)) delete p.communityId; });
      d.memberships = d.memberships.filter(m => m.userId !== me.id && !owned.has(m.communityId));
      d.ideas = d.ideas.filter(i => i.authorId !== me.id);
      d.notifications = d.notifications.filter(n => n.userId !== me.id && n.actorId !== me.id);
      d.reports = d.reports.filter(r => r.reporterId !== me.id);
      delete d.meId;
    });
  }
  resetPreview(): void { this.state = seed(Date.now()); this.commit(() => {}); }
}

export function depthOf(db: DB, c: Comment): number {
  let depth = 0, cur: Comment | undefined = c;
  while (cur?.parentId && depth < 10) { cur = db.comments.find(x => x.id === cur!.parentId); depth++; }
  return depth;
}

export const store = new Store();
export function useDB(): DB { return useSyncExternalStore(store.subscribe, store.get, store.get); }
